import React, { ReactNode, useId } from 'react';
import classNames from 'classnames';

import './ListRow.scss';

interface ListRowProps {
  /** Primary label rendered at the start of the row. */
  label: ReactNode;
  /**
   * Interactive content pinned directly after the label (e.g., an info button). Kept outside
   * the row's own click surface so it can be focused and activated on its own.
   */
  labelAdornment?: ReactNode;
  /** Trailing content shown before the chevron (e.g., a price). */
  trailing?: ReactNode;
  /** `strong` renders a bold, prominent label; `regular` a lighter nested label. */
  emphasis?: 'strong' | 'regular';
  /** Indents the row to signal nesting under a preceding row. */
  indented?: boolean;
  /** Renders the trailing chevron affordance. Defaults to `true`. */
  chevron?: boolean;
  /** Accessible label when the visible `label` is not descriptive on its own. */
  ariaLabel?: string;
  className?: string;
  onClick?(): void;
}

/**
 * Generic clickable list row: a start-aligned label, optional trailing content, and a
 * trailing chevron affordance, with designed hover/focus states. Meant to sit inside a
 * `<ul>` — it renders its own `<li>` wrapper.
 *
 * The row's click surface is a full-bleed `<button>` layered behind the content rather than a
 * wrapper around it, so `labelAdornment` can hold its own button without nesting one inside
 * another. `onClick` sits on the row, so activating the surface — by mouse or keyboard —
 * bubbles up to it; an adornment stops propagation to keep the row's action from firing too.
 *
 * On hover/focus the row tints and drives three inherited custom properties
 * (`--row-label-color`, `--row-value-color`, `--row-muted-color`) so trailing content
 * rendered by a different component (e.g., a price) can recolor in step without coupling.
 */
export const ListRow = ({
  label,
  labelAdornment,
  trailing,
  emphasis = 'regular',
  indented = false,
  chevron = true,
  ariaLabel,
  onClick,
  className
}: ListRowProps) => {
  const generatedId = useId();
  const labelId = `list-row-label-${generatedId}`;

  return (
    <li styleName="list-row__wrap">
      <div
        className={className}
        styleName={classNames('list-row', {
          'list-row--strong': emphasis === 'strong',
          'list-row--regular': emphasis === 'regular',
          'list-row--indented': indented
        })}
        onClick={onClick}
      >
        <button
          type="button"
          styleName="list-row__surface"
          aria-label={ariaLabel}
          aria-labelledby={ariaLabel ? undefined : labelId}
        />
        <span styleName="list-row__label-group">
          <span id={labelId} styleName="list-row__label">{label}</span>
          {labelAdornment}
        </span>
        {trailing && <span styleName="list-row__trailing">{trailing}</span>}
        {chevron && (
          <span
            className="dashicon dashicon-chevron-right"
            styleName="list-row__chevron"
            aria-hidden="true"
          />
        )}
      </div>
    </li>
  );
};

export default ListRow;
